/**
 * 사용자 전환 시 stale 데이터 정리 유틸.
 *
 * useAuth.signIn 을 우회하는 진입점(SignInForm, SignUpForm, NaverCallback,
 * SessionKeeper 등) 도 호출 가능하도록 hook 외부로 추출.
 *
 * 정리 대상:
 * - Redux 메모리 (base.common)
 * - redux-persist `persist:datamodeler` (auth/locale/daExtension)
 * - 레거시 `persist:datamodeler` 의 base.common.multiModelData
 * - undo/redo IndexedDB (`ModelHistoryDB`)
 * - 모델 복구용 IndexedDB (`ModelDataDB` 'current-session') — 사용자 비분리 저장이라
 *   사용자 전환 시 반드시 정리 (이전 사용자 모델이 신규 사용자에게 노출되는 것 방지)
 */

import { persistor } from 'store/index';
import { resetState } from 'store/base/commonSlice';
import { historyDB } from 'utils/historyDB';
import { clearBaseDataOnly } from 'utils/indexedDBPreview';
import { modelDataDB } from 'utils/modelDataDB';

/**
 * 모든 user-scoped 저장소를 정리.
 * @param {Function} dispatch - redux dispatch
 */
export const purgeUserScopedData = async (dispatch) => {
  // 1) Redux 메모리 정리
  try { dispatch(resetState()); } catch (e) { console.warn('resetState failed:', e); }

  // 2) redux-persist 저장 정리
  try {
    await persistor.purge();
  } catch (storageError) {
    console.warn('persistor.purge failed, falling back to manual removal:', storageError);
    try { localStorage.removeItem('persist:root'); } catch {}
    try { localStorage.removeItem('admin'); } catch {}
  }

  // 3) 레거시 base.common.multiModelData 가 'persist:datamodeler' 에 남아있을 수 있음
  try { await clearBaseDataOnly(); } catch (e) { console.warn('clearBaseDataOnly failed:', e); }

  // 4) undo/redo 히스토리 전체 청소
  try { await historyDB.clearAllHistories(); } catch (e) { console.warn('historyDB.clearAllHistories failed:', e); }

  // 5) ModelDataDB('current-session') 청소 — 모델 복구용 IndexedDB.
  //    사용자 비분리 단일 키 저장이므로, 정리하지 않으면 다음 로그인 시
  //    AppInitializer 가 이전 사용자의 모델들을 그대로 복구함.
  try { await modelDataDB.clearModels(); } catch (e) { console.warn('modelDataDB.clearModels failed:', e); }
};

/**
 * 들어오는 user 가 현재 user 와 다르면 stale 저장소를 정리.
 * - 두 userId 모두 비어있으면 no-op.
 * - 들어오는 userId 가 비어있고(=정보 미상) 기존 userId 가 있으면 보수적으로 정리.
 *
 * @param {Function} dispatch
 * @param {string} currentUserId
 * @param {string} incomingUserId
 */
export const purgeIfUserChanged = async (dispatch, currentUserId, incomingUserId) => {
  const prev = currentUserId || '';
  const next = incomingUserId || '';
  if (prev && next && prev !== next) {
    await purgeUserScopedData(dispatch);
  } else if (prev && !next) {
    await purgeUserScopedData(dispatch);
  }
};
